import * as React from 'react';

import type { ProductCard as ProductCardType } from 'libs/apis/generated/models/ProductCard';

import classes from './ProductCard.scss';

type Props = {
  product: ProductCardType;
  isPublished: boolean;
  deviceType: 'sp' | 'pc';
  renderAction?: (product: ProductCardType) => React.ReactNode;
  shouldShowFavoriteLoginModal?: boolean;
};

const isNewPublished = (published: string | undefined): boolean => {
  if (!published) {
    return false;
  }
  const publishedDate = new Date(published);
  if (Number.isNaN(publishedDate.getTime())) {
    return false;
  }
  const thresholdDate = new Date();
  thresholdDate.setDate(thresholdDate.getDate() - 30);
  thresholdDate.setHours(0, 0, 0, 0);
  return publishedDate >= thresholdDate;
};

const createProductItemProps = (product: ProductCardType, deviceType: 'sp' | 'pc'): Record<string, any> => {
  const brandTitle = product.brands.map((brand) => brand.title).join(', ');
  const props: Record<string, any> = {
    'product-id': product.id,
    title: product.title,
    'brand-title': brandTitle,
    'product-group': product.productGroupName ?? '',
    price: product.price,
    'image-url': product.imageUrl,
    'min-shipping-date': product.minShippingDate ?? '',
    device: deviceType,
  };
  if (product.shouldDisplayPriceRangeMark) {
    props['should-display-price-range-mark'] = '';
  }
  if (isNewPublished(product.published)) {
    props['is-new'] = '';
  }
  if (product.canPersonalization) {
    props['can-personalization'] = '';
  }
  if (product.stock <= 0) {
    props['sold-out'] = '';
  }
  return props;
};

const createFavButtonProps = (
  product: ProductCardType,
  isPublished: boolean,
  deviceType: 'sp' | 'pc',
  shouldShowFavoriteLoginModal: boolean
): Record<string, any> => ({
  slot: 'fav-button',
  'product-id': product.id,
  favorited: product.isFavorite,
  'is-published': isPublished,
  mock: false,
  'show-modal-on-click': shouldShowFavoriteLoginModal,
  device: deviceType,
  'log-add-fav-event': 'add_favorite_from_product_list',
  'log-delete-fav-event': 'delete_favorite_from_product_list',
});

const ProductCard: React.FC<Props> = ({
  product,
  isPublished,
  deviceType,
  renderAction,
  shouldShowFavoriteLoginModal = false,
}) => {
  const productItemProps = createProductItemProps(product, deviceType);
  const favButtonProps = createFavButtonProps(product, isPublished, deviceType, shouldShowFavoriteLoginModal);
  const action = renderAction ? renderAction(product) : null;
  const productItem = React.createElement(
    'product-item',
    { ...productItemProps },
    React.createElement('fav-button', favButtonProps)
  );

  return (
    <div className={classes['product-wrapper']}>
      {productItem}
      {action ? <div className={classes['product-wrapper__action']}>{action}</div> : null}
    </div>
  );
};

export default ProductCard;
